import React from "react";
import { Table, Spin, Progress, Tag } from "antd";
import { ITaskOrder, TaskOrderStatus } from "../../types/taskOrder.type";
import dayjs from "dayjs";
import { useAuth } from "../../contexts/auth.context";
import { UserRole } from "../../types/user.type";

interface TaskOrdersRes {
  data: ITaskOrder[] | null;
  loading: boolean;
}

export default function TaskOrders({ data, loading }: TaskOrdersRes) {
  const { authState } = useAuth();
  let height = "300px";

  if (
    authState.user?.role === UserRole.Foreman ||
    authState.user?.role === UserRole.SalesDept
  ) {
    height = "660px";
  }

  if (loading) {
    return <Spin size="large" />;
  }

  if (!data) {
    return <div>No data available</div>;
  }

  const statusColors: Record<TaskOrderStatus, string> = {
    [TaskOrderStatus.Pending]: "gold",
    [TaskOrderStatus.InProgress]: "blue",
    [TaskOrderStatus.OnHold]: "orange",
    [TaskOrderStatus.Rejected]: "red",
    [TaskOrderStatus.Completed]: "green",
  };

  const columns = [
    {
      title: "Название",
      dataIndex: "produceName",
      key: "produceName",
    },
    {
      title: "Кол-во",
      dataIndex: "total",
      key: "total",
    },
    {
      title: "Производство",
      key: "production",
      render: (_: any, order: ITaskOrder) => (
        <div style={{ minWidth: 120 }}>
          <div style={{ marginBottom: 4 }}>
            <span style={{ fontSize: 12, color: '#666' }}>
              {order.produced || 0} / {order.total}
            </span>
          </div>
          <Progress 
            percent={order.productionProgress || 0} 
            size="small" 
            status={order.productionProgress === 100 ? "success" : "active"}
          />
        </div>
      ),
    },
    {
      title: "Выдача",
      key: "delivery",
      render: (_: any, order: ITaskOrder) => (
        <div style={{ minWidth: 120 }}>
          <div style={{ marginBottom: 4 }}>
            <span style={{ fontSize: 12, color: '#666' }}>
              {order.delivered || 0} / {order.total}
            </span>
          </div>
          <Progress 
            percent={order.deliveryProgress || 0} 
            size="small" 
            status={order.deliveryProgress === 100 ? "success" : "active"}
            strokeColor={order.deliveryProgress === 100 ? "#52c41a" : "#1890ff"}
          />
        </div>
      ),
    },
    {
      title: "Приоритет",
      dataIndex: "priority",
      key: "priority",
      render: (priority: number) => (
        <Tag color={priority === 0 ? "red" : priority <= 5 ? "orange" : "green"}>
          {priority === 0 ? "Высокий" : priority <= 5 ? "Средний" : "Низкий"}
        </Tag>
      ),
    },
    {
      title: "Валюта",
      dataIndex: "currency",
      key: "currency",
    },
    {
      title: "Сумма",
      dataIndex: "totalCost",
      key: "totalCost",
    },
    {
      title: "Имя клиента",
      dataIndex: "clientName",
      key: "clientName",
    },
    {
      title: "Статус",
      dataIndex: "status",
      key: "status",
      render: (value: TaskOrderStatus) => (
        <Tag color={statusColors[value] || "default"}>{value}</Tag>
      ),
    },
  ];

  const rowClassName = (record: ITaskOrder) => {
    if (record.status === TaskOrderStatus.Completed) {
      return "row-green";
    }

    if (record.status === TaskOrderStatus.Rejected) {
      return "row-red";
    }

    if (record.status === TaskOrderStatus.OnHold) {
      return "row-yellow";
    }

    return "";
  };

  return (
    <div style={{ padding: "20px" }}>
      <h2>Задачи-Заказы</h2>

      <div style={{ marginTop: "20px", height, overflowY: "auto" }}>
        <Table
          dataSource={data}
          columns={columns}
          rowKey={(record) => record._id}
          pagination={false}
          scroll={{ y: height }}
          style={{ height }}
          rowClassName={rowClassName}
        />
      </div>
    </div>
  );
}
